import { Directive, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NEVER, Observable, Subscription, combineLatest } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Entity } from '../models';
import { EntityFormService } from './entity-form.service';
import { UnauthorizedError, UnprocessableError } from './http.service';
import { MessageEventService } from './message-event.service';
import { SingletonEntityService } from './singleton-entity.service';

@Directive()
export abstract class ReactiveSingletonEntityEditPageComponent<
  E extends Entity,
  F extends { [K in keyof F]: AbstractControl<any, any> } = any,
  D = any
> implements OnInit, OnDestroy
{
  formData$: Observable<{ entity: E; data: D }>;
  item$: Observable<E>;
  loading = false;
  pending = false;
  unauthorized = false;

  get form(): FormGroup<F> {
    return this.formService.form;
  }

  private subscription = new Subscription();

  constructor(
    protected entityService: SingletonEntityService<E>,
    protected messageService: MessageEventService,
    protected router: Router,
    protected titleService: Title,
    protected formService: EntityFormService<E, F, D>
  ) {}

  protected abstract getEntityUpdatedMessage(entity: E): string;

  protected abstract getPostEditRoute(updatedEntity: E): string[];

  protected abstract getBaseTitle(): string;

  protected abstract getTitle(entity: E): string;

  protected abstract cancelEdit();
  protected abstract handleLoadedFormData(entity: E, data: D);

  protected mapErrorsToForm(errors: Record<string, string>) {
    Object.entries(errors).forEach(([key, value]) => {
      this.form.get(key).setErrors({
        api: value,
      });
    });
  }

  editEntity() {
    if (this.form.valid && !this.pending) {
      this.pending = true;
      this.subscription.add(
        this.item$
          .pipe(
            switchMap((entity) => {
              entity = this.formService.buildEntity(entity);
              return this.entityService.updateEntity(entity).pipe(
                catchError((error: Error) => {
                  if (error instanceof UnprocessableError) {
                    this.mapErrorsToForm(error.errors);
                  } else if (error instanceof UnauthorizedError) {
                    this.unauthorized = true;
                  }
                  this.pending = false;
                  return NEVER;
                })
              );
            })
          )
          .subscribe((updatedEntity: E) => {
            this.messageService.pushEvent({
              message: this.getEntityUpdatedMessage(updatedEntity),
              status: 'success',
            });
            this.pending = false;

            this.router.navigate(this.getPostEditRoute(updatedEntity));
          })
      );
    }
  }

  protected loadEntity(): Observable<E> {
    return this.entityService.getEntity();
  }

  ngOnInit() {
    this.loading = true;
    this.titleService.setTitle(this.getBaseTitle() + ' | Kahi');

    this.formData$ = combineLatest([
      this.loadEntity(),
      this.formService.additionalFormData$,
    ]).pipe(
      map(([entity, data]) => ({ entity, data })),
      catchError((error: Error) => {
        if (error instanceof UnauthorizedError) {
          this.unauthorized = true;
        }
        this.loading = false;
        return NEVER;
      }),
      tap(({ entity, data }) => {
        this.handleLoadedFormData(entity, data);
        this.titleService.setTitle(this.getTitle(entity));
        this.loading = false;
      }),
      shareReplay(1)
    );

    this.item$ = this.formData$.pipe(map(({ entity }) => entity));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
